import React, { useCallback, useState } from 'react';
import { Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Employee } from '..';
import ServerError from '../../../../../components/ServerError';
import { useToasts } from '../../../../../context/Toast';
import { useFlairPersistentFilter } from '../../../../../hooks/useFlairPersistentFilter';
import { useMutationErrorHandler } from '../../../../../hooks/useMutationErrorHandler';
import { SkeletonTable } from '../../../components/Skeleton/Table';
import { CertificateDeleteConfirmation } from '../../../pages/Certificates/DeleteConfirmations';
import { applyCertificateFilter } from '../../../pages/Certificates/filters';
import {
  CertificatesFilter,
  collectExpirationDateYearFilterOptions,
  collectIssueDateYearFilterOptions,
  collectIssuedByFilterOptions,
} from '../../../pages/Certificates/Filters/CertificatesFilter';
import { mapCertificate } from '../../../pages/Certificates/mappings';
import { MyCertificatesTable } from '../../../pages/Certificates/MyCertificatesTable';
import {
  Certificate,
  emptyCertificateFilter,
} from '../../../pages/Certificates/types';
import {
  useDeleteEmployeeCertificateByManagerMutation,
  useEmployeePageCertificateQuery,
} from '../../../__generated__/graphql';

import { employeePageRoutes } from '../PageHeader/routes';
import { CertificateModal } from './CertificateModal';

type Props = {
  employee: Employee;
};

const EmployeeCertificates: React.FC<Props> = ({ employee }) => {
  const { t } = useTranslation();
  const { data, loading, error } = useEmployeePageCertificateQuery({
    variables: { employeeId: employee.Id },
  });

  const [deleteEmployeeCertificate, { loading: deleteInProgress }] =
    useDeleteEmployeeCertificateByManagerMutation();

  const errorHandler = useMutationErrorHandler();

  const { addSuccess, addError } = useToasts();
  const [deleteItemId, setDeleteItemId] = useState<string | null>(null);
  const [saveModalVisible, setModalBarVisible] = useState<boolean>(false);
  const [selectedCertificate, setSelectedCertificate] = useState<string | null>(
    null,
  );

  const certificateDelete = useCallback(async () => {
    if (deleteItemId === null) {
      return;
    }
    await deleteEmployeeCertificate({
      variables: {
        id: deleteItemId,
        employeeId: employee.Id,
      },
    })
      .then((r) => {
        if (r.data?.certificates.deleteEmployeeCertificate.error) {
          addError(r.data?.certificates.deleteEmployeeCertificate.error);
          return;
        }
        addSuccess(t('expenses.expenseDeletedToast'));
      })
      .catch(errorHandler)
      .finally(() => {
        setDeleteItemId(null);
      });
  }, [
    addError,
    addSuccess,
    deleteEmployeeCertificate,
    deleteItemId,
    employee.Id,
    errorHandler,
    t,
  ]);
  const handleCreateBtnClick = useCallback(() => {
    setModalBarVisible(true);
  }, []);

  const handleSaveSidebarClose = useCallback(() => {
    setModalBarVisible(false);
    setSelectedCertificate(null);
  }, []);

  const [filter, setFilter] = useFlairPersistentFilter({
    defaultFilter: emptyCertificateFilter,
    storageKey: 'employee_certificate_filter',
    createRouteUrl: (queryParams) =>
      employeePageRoutes()
        .employeeCertificates.route.withQueryParams(queryParams)
        .create({ employeeId: employee.Id }),
  });

  if (error) {
    return <ServerError />;
  }

  let allCertificates: Certificate[] =
    data?.manager.employee?.employeeCertificates.map(mapCertificate) || [];

  const handleDelete = (certificateId: string) => {
    setDeleteItemId(certificateId);
  };

  const handleDeleteClose = () => {
    setDeleteItemId(null);
  };

  const handleEdit = (certificateId: string) => {
    setSelectedCertificate(certificateId);
    setModalBarVisible(true);
  };

  const renderContent = () => {
    if (loading || !data) {
      return <Loading />;
    }

    const certificates = applyCertificateFilter(filter, allCertificates);

    return (
      <MyCertificatesTable
        certificates={certificates}
        onDelete={handleDelete}
        onEdit={handleEdit}></MyCertificatesTable>
    );
  };

  let selectedItem =
    allCertificates?.find((x) => x.id === selectedCertificate) ?? null;

  return (
    <>
      <Card>
        <Card.Header className="h-auto">
          <CertificatesFilter
            filter={filter}
            issuedByOptions={collectIssuedByFilterOptions(allCertificates)}
            issueDateYearOptions={collectIssueDateYearFilterOptions(
              allCertificates,
            )}
            expirationDateYearOptions={collectExpirationDateYearFilterOptions(
              allCertificates,
            )}
            onChange={setFilter}
            onCreateBtnClick={handleCreateBtnClick}></CertificatesFilter>
        </Card.Header>
        {renderContent()}
      </Card>

      <CertificateDeleteConfirmation
        visible={!!deleteItemId}
        submitInProgress={deleteInProgress}
        onClose={handleDeleteClose}
        onSubmit={certificateDelete}
      />
      <CertificateModal
        show={saveModalVisible}
        onSave={handleSaveSidebarClose}
        onClose={handleSaveSidebarClose}
        certificate={selectedItem}
        employeeId={employee.Id}
      />
    </>
  );
};

const Loading: React.FC = () => {
  return <SkeletonTable columns={5} rows={5} />;
};

export default EmployeeCertificates;
